import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Zap, Download } from "lucide-react";
import api from "@/lib/api";
import { T } from "@/constants/testIds";
import { toast } from "sonner";
import { CandidateDrawer } from "@/components/CandidateDrawer.jsx";

const fmtMs = (ms) => {
  if (!ms) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

export default function FormResponses() {
  const { formId } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [drawer, setDrawer] = useState(null);
  const onChanged = (s) => { setSubs((p) => p.map((x) => (x.submission_id === s.submission_id ? s : x))); setDrawer(s); };

  const load = async () => {
    try {
      const [a, s, f] = await Promise.all([
        api.get(`/forms/${formId}/analytics`),
        api.get(`/submissions`, { params: { form_id: formId } }),
        api.get(`/forms/${formId}`),
      ]);
      setAnalytics(a.data);
      setSubs(s.data);
      setForm(f.data);
    } catch { toast.error("Failed to load responses"); }
  };
  useEffect(() => { load(); }, [formId]);

  const partials = useMemo(() => subs.filter((s) => s.partial && s.email), [subs]);

  const nudge = async () => {
    const ids = picked.size > 0 ? [...picked] : partials.map((p) => p.submission_id);
    if (ids.length === 0) return toast.warning("No partial submissions to nudge");
    try {
      const r = await api.post(`/submissions/nudge`, { submission_ids: ids });
      toast.success(`Nudge queued for ${r.data.sent} candidate${r.data.sent === 1 ? "" : "s"}`);
      setPicked(new Set());
    } catch { toast.error("Nudge failed"); }
  };

  const togglePick = (id) => {
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/forms" className="tt-btn-ghost p-1.5"><ArrowLeft size={16} strokeWidth={1.75} /></Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{form?.title || "Form"}</h1>
          <div className="text-xs font-mono text-[#71717A] mt-0.5">/{form?.slug}</div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KPI label="Views" value={analytics?.kpi.views ?? 0} testId={T.responses.kpiViews} />
        <KPI label="Completion" value={`${analytics?.kpi.completion_rate ?? 0}%`} testId={T.responses.kpiCompletion} />
        <KPI label="Median time" value={fmtMs(analytics?.kpi.median_time_ms)} testId={T.responses.kpiMedian} />
        <KPI label="Partials" value={analytics?.kpi.partials ?? 0} testId={T.responses.kpiPartials} accent="warn" />
        <KPI label="Calls booked" value={analytics?.kpi.booked ?? 0} testId={T.responses.kpiBooked} accent="pass" />
      </div>

      {/* Funnel */}
      <div className="tt-card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="tt-label">Funnel</div>
          {analytics?.drop_off && analytics.drop_off.drop > 0 && (
            <div className="tt-badge-warn">Biggest drop: Step {analytics.drop_off.from_step} → {analytics.drop_off.to_step} (−{analytics.drop_off.drop})</div>
          )}
        </div>
        <div className="space-y-2">
          {analytics?.funnel?.map((f, i) => {
            const prev = i > 0 ? analytics.funnel[i - 1].reached : (analytics.kpi.views || f.reached);
            const pct = prev ? (f.reached / prev) * 100 : 0;
            const dropPct = prev ? ((prev - f.reached) / prev) * 100 : 0;
            return (
              <div key={f.step} className="flex items-center gap-3">
                <div className="font-mono text-xs text-[#71717A] w-16">Step {f.step}</div>
                <div className="flex-1 h-7 bg-[#121212] border border-[#262626] rounded overflow-hidden relative">
                  <div
                    className="h-full bg-[#5B9BFF]/40 border-r border-[#5B9BFF]"
                    style={{ width: `${Math.max(2, pct)}%` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-mono">
                    <span className="text-[#F4F4F5]">{f.reached}</span>
                    {dropPct > 0 && i > 0 && (
                      <span className="text-[#F5A524]">−{dropPct.toFixed(0)}%</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sources + Cities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="tt-card p-5">
          <div className="tt-label mb-3">Source breakdown</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#71717A] text-xs font-mono">
                <th className="text-left pb-2">Source</th>
                <th className="text-right pb-2">Views</th>
                <th className="text-right pb-2">Started</th>
                <th className="text-right pb-2">Submitted</th>
                <th className="text-right pb-2">Booked rate</th>
              </tr>
            </thead>
            <tbody>
              {(analytics?.sources || []).map((s) => {
                const rate = s.started ? ((s.booked / s.started) * 100).toFixed(0) : "0";
                return (
                  <tr key={s.source} className="border-t border-[#1E1E1E]">
                    <td className="py-2">{s.source}</td>
                    <td className="py-2 text-right font-mono">{s.views}</td>
                    <td className="py-2 text-right font-mono">{s.started}</td>
                    <td className="py-2 text-right font-mono">{s.submitted}</td>
                    <td className="py-2 text-right font-mono text-[#5B9BFF]">{rate}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="tt-card p-5">
          <div className="tt-label mb-3">Top cities</div>
          <div className="space-y-2">
            {(analytics?.cities || []).map((c) => (
              <div key={c.city} className="flex items-center justify-between text-sm">
                <span>{c.city}</span>
                <span className="font-mono text-[#A1A1AA]">{c.count}</span>
              </div>
            ))}
            {(analytics?.cities || []).length === 0 && <div className="text-sm text-[#71717A]">No city data yet.</div>}
          </div>
        </div>
      </div>

      {/* Submissions table */}
      <div className="tt-card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E1E1E]">
          <div className="tt-label">Submissions · {subs.length}</div>
          <div className="flex gap-2">
            <button
              data-testid={T.responses.nudgeBtn}
              onClick={nudge}
              className="tt-btn-secondary text-xs flex items-center gap-2"
            >
              <Zap size={13} strokeWidth={1.75} /> Nudge partials
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-[#0F0F0F]">
              <tr className="text-[#71717A] text-xs font-mono">
                <th className="text-left px-4 py-2 w-8"></th>
                <th className="text-left px-4 py-2">Candidate</th>
                <th className="text-left px-4 py-2">City</th>
                <th className="text-left px-4 py-2">Source</th>
                <th className="text-right px-4 py-2">Time to fill</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">AI</th>
                <th className="text-left px-4 py-2">Next</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.submission_id} data-testid={T.responses.row(s.submission_id)} className="border-t border-[#1E1E1E]">
                  <td className="px-4 py-3">
                    {s.partial && s.email && (
                      <input
                        type="checkbox"
                        checked={picked.has(s.submission_id)}
                        onChange={() => togglePick(s.submission_id)}
                        className="accent-[#F5A524]"
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => setDrawer(s)} data-testid={`response-open-${s.submission_id}`}>
                    <div className="font-medium hover:text-[#5B9BFF]">{s.name || <span className="text-[#71717A]">Anonymous</span>}</div>
                    <div className="text-xs font-mono text-[#71717A]">{s.email || "—"}</div>
                  </td>
                  <td className="px-4 py-3">{s.city || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#A1A1AA]">{(s.utm?.source) || "direct"}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMs(s.total_time_ms)}</td>
                  <td className="px-4 py-3">
                    {s.partial
                      ? <span className="tt-badge-warn">Partial · step {s.last_step_reached}</span>
                      : s.knockout
                        ? <span className="tt-badge-danger">Knockout</span>
                        : <span className="tt-badge-info">{s.stage}</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {typeof s.ai_score === "number" ? (
                      <span className={s.ai_score >= 70 ? "text-[#22C55E]" : s.ai_score >= 50 ? "text-[#F5A524]" : "text-[#EF4444]"}>
                        {s.ai_score.toFixed(0)}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#A1A1AA]">
                    {s.partial && s.email ? "Send nudge" : s.booking ? "Interview scheduled" : "Review"}
                  </td>
                </tr>
              ))}
              {subs.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#71717A]">No submissions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {drawer && <CandidateDrawer sub={drawer} form={form} onClose={() => setDrawer(null)} onChanged={onChanged} onMessage={() => toast.info("Use Hiring → Candidates to message this candidate")} />}
    </div>
  );
}

function KPI({ label, value, testId, accent }) {
  const color =
    accent === "warn" ? "text-[#F5A524]" :
    accent === "pass" ? "text-[#22C55E]" :
    "text-[#F4F4F5]";
  return (
    <div data-testid={testId} className="tt-card p-4">
      <div className="tt-label mb-2">{label}</div>
      <div className={`font-mono text-2xl font-semibold tracking-tight ${color}`}>{value}</div>
    </div>
  );
}
